// src/data/warRoom.js
//
// Track 6 item 6a-04, second half (2026-09-14, task list section 230; ruling 131). Ruling 17, Bob's
// words: "The War Room will be the General Chair's meeting room, where only invitees may be present."
// data\YCP_LC_GC_WORKSPACE_REQUIREMENTS_2026-08-23.md SS4: convened by the General Chairperson;
// invitation is PER OCCASION, not per role - a Local Chairperson is not automatically present; an
// invitee list, a convening act, and a record of who was present.
//
// One document per occasion in warRoomSessions/{id}. The rules for it hold the same line server-side:
// the GC of the committee convenes and closes; an invitee reads the occasion he is invited to and
// records his own presence, once; nobody else sees that the occasion exists. Pure module - the page
// draws and writes, and decides nothing.

export const STATES = { CONVENED: 'convened', CLOSED: 'closed' };
export const TITLE_MAX = 120;
export const AGENDA_MAX = 2000;

// SECTION 527 (2026-09-30, Bob during the GO-342/GO-341 call: "add zoom team meetings to the gcs warroom").
// An occasion may carry an online meeting link - Zoom, Microsoft Teams or Google Meet - and a start time, so the
// invitees join from the occasion itself. The link is only ever one of those three services, over https; anything
// else is refused, because an invitee clicks it from inside the platform and trusts where it goes.
const MEETING_HOSTS = [
  { provider: 'Zoom', test: (h) => h === 'zoom.us' || h.endsWith('.zoom.us') || h === 'zoomgov.com' || h.endsWith('.zoomgov.com') },
  { provider: 'Microsoft Teams', test: (h) => h === 'teams.microsoft.com' || h.endsWith('.teams.microsoft.com') || h === 'teams.live.com' },
  { provider: 'Google Meet', test: (h) => h === 'meet.google.com' },
];
export const MEETING_URL_MAX = 500;
export function meetingLink(raw) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s) return { ok: true, url: null, provider: null };
  if (s.length > MEETING_URL_MAX) return { ok: false, error: 'The meeting link is too long.' };
  let u;
  try { u = new URL(s); } catch (e) { return { ok: false, error: 'The meeting link is not a web address. Paste the whole invitation link, starting with https://' }; }
  if (u.protocol !== 'https:') return { ok: false, error: 'The meeting link must start with https://' };
  const host = u.hostname.toLowerCase();
  const hit = MEETING_HOSTS.find(m => m.test(host));
  if (!hit) return { ok: false, error: 'Only a Zoom, Microsoft Teams or Google Meet link can be attached (this one is ' + host + ').' };
  return { ok: true, url: u.toString(), provider: hit.provider };
}
function isTime(s) { return typeof s === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(s); }

// The convener adds, changes or removes the link and time of an open occasion.
export function meetingPatch(session, user, rawUrl, time) {
  const errors = [];
  if (!session || !user) errors.push('no session');
  else {
    if (session.state !== STATES.CONVENED) errors.push('This occasion is closed.');
    if (!(session.convenedBy === user.uid || user.isAdmin)) errors.push('Only the convener sets the meeting link.');
  }
  const m = meetingLink(rawUrl);
  if (!m.ok) errors.push(m.error);
  if (time != null && time !== '' && !isTime(time)) errors.push('The time must be HH:MM.');
  if (errors.length) return { ok: false, errors, patch: null };
  return { ok: true, errors: [], patch: { meetingUrl: m.url, meetingProvider: m.provider, meetingTime: time || null } };
}

function isDate(s) { return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s + 'T00:00:00Z')); }

export function mayConvene(user, committee) {
  if (!user || !committee) return false;
  if (user.isAdmin) return true;
  return user.role === 'general_chair' && user.committee === committee;
}

// PLAN B (2026-09-19, master list 10.13, ruling 169). WHICH COMMITTEE'S ROOM this user is in. A General
// Chairperson - or anyone else with a committee - is always in his own; a pick is ignored. The administrator
// holds no committee (ruling 45), so he is in the one he PICKED, and in none until he picks. Before this,
// the page read only user.committee, the administrator's was empty, and ruling 169's build access showed
// him a lock screen.
export function warRoomCommittee(user, picked) {
  if (!user) return null;
  if (user.committee) return user.committee;
  if (user.isAdmin && typeof picked === 'string' && picked.trim()) return picked.trim();
  return null;
}

// Who may be in the room for THIS occasion: the convener, admin, and the invitees - by account.
export function mayEnter(session, user) {
  if (!session || !user || !user.uid) return false;
  if (user.isAdmin) return true;
  if (session.convenedBy === user.uid) return true;
  return Array.isArray(session.inviteeUids) && session.inviteeUids.includes(user.uid);
}

// The convening act. Invitees are chosen from the committee's published seats (committees/{id}.seats)
// so each is addressed by account; the convener is present by definition and is not an invitee.
export function convene(user, committee, input, seats) {
  const i = input || {};
  const errors = [];
  if (!mayConvene(user, committee)) errors.push('Only the General Chairperson of ' + (committee || 'this committee') + ' convenes its War Room.');
  const title = String(i.title || '').trim();
  if (!title) errors.push('An occasion has a title.');
  if (title.length > TITLE_MAX) errors.push('The title is limited to ' + TITLE_MAX + ' characters.');
  if (i.scheduledFor != null && i.scheduledFor !== '' && !isDate(i.scheduledFor)) errors.push('The date must be YYYY-MM-DD.');
  const agenda = String(i.agenda || '').trim();
  if (agenda.length > AGENDA_MAX) errors.push('The agenda is limited to ' + AGENDA_MAX + ' characters.');
  const wanted = Array.isArray(i.inviteeUids) ? i.inviteeUids.filter(Boolean) : [];
  const invitees = [];
  for (const uid of Array.from(new Set(wanted))) {
    if (uid === user.uid) { errors.push('The convener is present by definition; he is not an invitee.'); continue; }
    const seat = (seats || []).find(s => s && s.uid === uid);
    if (!seat) { errors.push('Invitee ' + uid + ' is not a published seat of ' + committee + ' - invitees are addressed by account (committees/' + committee + '.seats).'); continue; }
    invitees.push({ uid: seat.uid, name: seat.name || '', email: seat.email || '', role: seat.role || '', scopeKey: seat.scopeKey || null });
  }
  if (!invitees.length && !errors.length) errors.push('Invite at least one person, or there is no meeting.');
  const meet = meetingLink(i.meetingUrl);   // section 527
  if (!meet.ok) errors.push(meet.error);
  if (i.meetingTime != null && i.meetingTime !== '' && !isTime(i.meetingTime)) errors.push('The time must be HH:MM.');
  if (errors.length) return { ok: false, errors, record: null };
  return {
    ok: true, errors: [],
    record: {
      committee, title, agenda: agenda || null,
      scheduledFor: i.scheduledFor || null,
      meetingTime: i.meetingTime || null, meetingUrl: meet.url, meetingProvider: meet.provider,   // section 527
      state: STATES.CONVENED,
      convenedBy: user.uid, convenedByName: user.name || user.email || 'Unknown',
      convenedAt: i.at || new Date().toISOString(),
      invitees, inviteeUids: invitees.map(x => x.uid),
      attendance: [],          // [{ uid, name, at, recordedBy }] - who was present, as recorded
      closedAt: null, closedBy: null, closeNote: null,
    },
  };
}

// An invitee records his own presence, once. The convener may record any invitee's presence (a
// person in the room who cannot reach a keyboard is still present). Nobody records a non-invitee.
export function presencePatch(session, user, forUid) {
  const errors = [];
  const target = forUid || (user && user.uid);
  if (!session || !user) errors.push('no session');
  else {
    if (session.state !== STATES.CONVENED) errors.push('This occasion is closed; attendance is a record of the meeting, not an edit after it.');
    const isConvener = session.convenedBy === user.uid || !!user.isAdmin;
    if (target !== user.uid && !isConvener) errors.push('Only the convener records another person\'s presence.');
    if (!(Array.isArray(session.inviteeUids) && session.inviteeUids.includes(target)) && target !== session.convenedBy) errors.push('Not an invitee of this occasion.');
    if (Array.isArray(session.attendance) && session.attendance.some(a => a && a.uid === target)) errors.push('Presence is already recorded.');
  }
  if (errors.length) return { ok: false, errors, patch: null };
  const inv = (session.invitees || []).find(x => x && x.uid === target);
  const entry = { uid: target, name: inv ? inv.name : (target === session.convenedBy ? session.convenedByName : ''), at: new Date().toISOString(), recordedBy: user.uid };
  return { ok: true, errors: [], patch: { attendance: (session.attendance || []).concat([entry]) } };
}

export function closePatch(session, user, note) {
  const errors = [];
  if (!session || !user) errors.push('no session');
  else {
    if (!(session.convenedBy === user.uid || user.isAdmin)) errors.push('Only the convener closes the occasion.');
    if (session.state !== STATES.CONVENED) errors.push('Already closed.');
  }
  if (errors.length) return { ok: false, errors, patch: null };
  return { ok: true, errors: [], patch: { state: STATES.CLOSED, closedAt: new Date().toISOString(), closedBy: user.uid, closeNote: String(note || '').trim() || null } };
}

// The attendance record as a line: who was invited, who was present, who was not.
export function attendanceSummary(session) {
  if (!session) return '';
  const present = new Set((session.attendance || []).map(a => a && a.uid));
  const invited = session.invitees || [];
  const were = invited.filter(x => present.has(x.uid)).map(x => x.name || x.uid);
  const not = invited.filter(x => !present.has(x.uid)).map(x => x.name || x.uid);
  return 'Convened by ' + (session.convenedByName || session.convenedBy) + '. Invited ' + invited.length + '. Present: ' + (were.length ? were.join(', ') : 'none recorded') + '.' + (not.length ? ' Not recorded present: ' + not.join(', ') + '.' : '');
}

// RULING 169 (2026-09-17): the administrator's entry into a committee's War Room during the build,
// as the log records it. Only an administrator produces one; the record names him, the committee
// (or UNSCOPED when his account carries none), the moment, and the one reason the ruling allows.
export const ADMIN_ENTRY_REASON = 'build access';
export function adminEntry(user, committee, at) {
  if (!user || !user.isAdmin || !user.uid) return null;
  const when = at || new Date().toISOString();
  if (!/^\d{4}-\d{2}-\d{2}T/.test(String(when))) return null;
  return {
    uid: user.uid,
    name: user.name || user.email || user.uid,
    committee: committee || 'UNSCOPED',
    enteredAt: String(when),
    reason: ADMIN_ENTRY_REASON,
  };
}

export function partitionSessions(list) {
  const l = Array.isArray(list) ? list.slice() : [];
  const by = (a, b) => String(b.convenedAt || '') < String(a.convenedAt || '') ? -1 : 1;
  return { open: l.filter(s => s && s.state === STATES.CONVENED).sort(by), closed: l.filter(s => s && s.state === STATES.CLOSED).sort(by) };
}

// ============================================================================================================
// SECTION 532 (2026-09-30, Bob: "There has to be more to the warroom than this" ... "GO"). What a meeting room is
// for: the business on the table, what was said about it, what was decided, and who does what next.
//   1. Agenda items - each may carry a claim, so the claim is ON THE TABLE by name, not by memory.
//   3. Minutes - written per agenda item by the convener while the occasion is open; locked at close (the rules
//      refuse any change to a closed occasion, so the record is the record).
//   4. Decisions and votes - the motion, the tally, and who was present when it was taken.
//   5. Action items - owner, due date, status - in warRoomActions/{id}; each appears on its owner's desk until done.
// Pure: every check the page needs is here; the Firestore rules hold the same line server-side.
// ============================================================================================================
export const ITEM_TITLE_MAX = 200;
export const MINUTES_MAX = 5000;
export const MOTION_MAX = 500;
export const ACTION_TITLE_MAX = 300;
export const ACTION_STATUSES = ['open', 'in_progress', 'done'];
export const ACTION_STATUS_LABEL = { open: 'Open', in_progress: 'In progress', done: 'Done' };
export const RESULTS = ['carried', 'failed', 'tabled', 'agreed'];
export const RESULT_LABEL = { carried: 'Carried', failed: 'Failed', tabled: 'Tabled', agreed: 'Agreed by consensus' };
export const GENERAL_ITEM = 'general';   // minutes and decisions not tied to one agenda item

function uidOf(user) { return user && user.uid; }
function mayMinute(session, user) {
  const errors = [];
  if (!session || !user) errors.push('no session');
  else {
    if (session.state !== STATES.CONVENED) errors.push('This occasion is closed; its record is locked.');
    if (!(session.convenedBy === user.uid || user.isAdmin)) errors.push('Only the convener keeps the record of the meeting.');
  }
  return errors;
}
function nextId(prefix, list) {
  const n = (Array.isArray(list) ? list : []).reduce((m, x) => {
    const k = x && typeof x.id === 'string' && x.id.startsWith(prefix) ? parseInt(x.id.slice(prefix.length), 10) : 0;
    return Number.isFinite(k) && k > m ? k : m;
  }, 0);
  return prefix + (n + 1);
}

// How a claim is named on the table: its number, the claimant, where it stands. Never its content.
export function claimLabel(claim) {
  if (!claim) return '';
  const num = claim.claimNumber || claim.claimRef || claim.docId || claim.id || '';
  const who = claim.claimantName || '';
  const st = claim.status ? String(claim.status).replace(/_/g, ' ') : '';
  return [num ? 'Claim ' + num : 'Claim', who, st ? '(' + st + ')' : ''].filter(Boolean).join(' - ').replace(' - (', ' (');
}

// 1. An agenda item, optionally carrying a claim the convener can see.
export function agendaItemPatch(session, user, input) {
  const i = input || {};
  const errors = mayMinute(session, user);
  const claim = i.claim || null;
  const title = String(i.title || '').trim() || (claim ? claimLabel(claim) : '');
  if (!title) errors.push('An agenda item has a title, or a claim.');
  if (title.length > ITEM_TITLE_MAX) errors.push('An agenda item title is limited to ' + ITEM_TITLE_MAX + ' characters.');
  const items = (session && Array.isArray(session.agendaItems)) ? session.agendaItems : [];
  const claimId = claim ? String(claim.docId || claim.id || '') : null;
  if (claimId && items.some(x => x && x.claimId === claimId)) errors.push('That claim is already on the agenda.');
  if (errors.length) return { ok: false, errors, patch: null };
  const item = { id: nextId('a', items), title, claimId: claimId || null, claimLabel: claim ? claimLabel(claim) : null, addedAt: i.at || new Date().toISOString(), addedBy: uidOf(user) };
  return { ok: true, errors: [], patch: { agendaItems: items.concat([item]) }, item };
}

// 3. Minutes for one agenda item (or GENERAL_ITEM). Replaces that item's text; the rest of the map is kept.
export function minutesPatch(session, user, itemId, text) {
  const errors = mayMinute(session, user);
  const id = String(itemId || '');
  const items = (session && Array.isArray(session.agendaItems)) ? session.agendaItems : [];
  if (id !== GENERAL_ITEM && !items.some(x => x && x.id === id)) errors.push('No such agenda item.');
  const t = String(text == null ? '' : text);
  if (t.length > MINUTES_MAX) errors.push('Minutes for one item are limited to ' + MINUTES_MAX + ' characters.');
  if (errors.length) return { ok: false, errors, patch: null };
  const minutes = { ...((session && session.minutes) || {}) };
  if (t.trim()) minutes[id] = { text: t, by: uidOf(user), at: new Date().toISOString() };
  else delete minutes[id];
  return { ok: true, errors: [], patch: { minutes } };
}

// Who is present right now, as recorded: the attendance list, plus the convener when he recorded himself.
export function presentPeople(session) {
  return ((session && session.attendance) || []).filter(a => a && a.uid).map(a => ({ uid: a.uid, name: a.name || a.uid }));
}

// 4. A decision. A vote carries its tally, and the tally cannot exceed the people recorded present when it was taken.
export function decisionPatch(session, user, input) {
  const i = input || {};
  const errors = mayMinute(session, user);
  const motion = String(i.motion || '').trim();
  if (!motion) errors.push('A decision states what was decided.');
  if (motion.length > MOTION_MAX) errors.push('The motion is limited to ' + MOTION_MAX + ' characters.');
  const itemId = String(i.itemId || GENERAL_ITEM);
  const items = (session && Array.isArray(session.agendaItems)) ? session.agendaItems : [];
  if (itemId !== GENERAL_ITEM && !items.some(x => x && x.id === itemId)) errors.push('No such agenda item.');
  const result = String(i.result || '');
  if (!RESULTS.includes(result)) errors.push('The result is one of: ' + RESULTS.map(r => RESULT_LABEL[r]).join(', ') + '.');
  const present = presentPeople(session);
  const n = (v) => (v === '' || v == null) ? 0 : Number(v);
  const vote = !!i.vote;
  let tally = null;
  if (vote) {
    const f = n(i.for), a = n(i.against), ab = n(i.abstain);
    if (![f, a, ab].every(x => Number.isInteger(x) && x >= 0)) errors.push('Votes are whole numbers.');
    else {
      if (f + a + ab === 0) errors.push('A vote has at least one vote cast.');
      if (f + a + ab > present.length) errors.push('The tally (' + (f + a + ab) + ') is more than the ' + present.length + ' recorded present. Record presence first.');
      if (result === 'carried' && !(f > a)) errors.push('A motion carries only with more votes for than against.');
      if (result === 'failed' && f > a) errors.push('More votes for than against - the motion did not fail.');
    }
    tally = { for: f, against: a, abstain: ab };
  }
  if (errors.length) return { ok: false, errors, patch: null };
  const list = (session && Array.isArray(session.decisions)) ? session.decisions : [];
  const d = { id: nextId('d', list), itemId, motion, result, vote, tally, presentAtVote: present, recordedBy: uidOf(user), at: i.at || new Date().toISOString() };
  return { ok: true, errors: [], patch: { decisions: list.concat([d]) }, decision: d };
}

// 5. An action item: an owner (the convener or an invitee of this occasion), a due date, a status.
// The record goes to warRoomActions; the page adds createdAt = serverTimestamp().
export function actionRecord(session, user, input) {
  const i = input || {};
  const errors = mayMinute(session, user);
  const title = String(i.title || '').trim();
  if (!title) errors.push('An action item says what is to be done.');
  if (title.length > ACTION_TITLE_MAX) errors.push('An action item is limited to ' + ACTION_TITLE_MAX + ' characters.');
  if (i.due != null && i.due !== '' && !isDate(i.due)) errors.push('The due date must be YYYY-MM-DD.');
  const ownerUid = String(i.ownerUid || '');
  let ownerName = '';
  if (session) {
    if (ownerUid && ownerUid === session.convenedBy) ownerName = session.convenedByName || '';
    else {
      const inv = (session.invitees || []).find(x => x && x.uid === ownerUid);
      if (inv) ownerName = inv.name || inv.email || ownerUid;
      else errors.push('The owner is someone in this occasion - the convener or an invitee.');
    }
  }
  const itemId = String(i.itemId || GENERAL_ITEM);
  const items = (session && Array.isArray(session.agendaItems)) ? session.agendaItems : [];
  if (itemId !== GENERAL_ITEM && !items.some(x => x && x.id === itemId)) errors.push('No such agenda item.');
  if (errors.length) return { ok: false, errors, record: null };
  const item = items.find(x => x && x.id === itemId) || null;
  return {
    ok: true, errors: [],
    record: {
      sessionId: session.id, sessionTitle: session.title || '', committee: session.committee,
      itemId, itemTitle: item ? item.title : null, claimId: item ? (item.claimId || null) : null,
      title, due: i.due || null, ownerUid, ownerName,
      visibleTo: Array.from(new Set([session.convenedBy].concat(session.inviteeUids || []))),
      status: 'open', statusNote: null, statusBy: uidOf(user),
      createdBy: uidOf(user), history: [],
    },
  };
}

// The owner (or the General Chairperson of the committee, or admin) moves an action item along.
export function actionStatusPatch(action, user, status, note) {
  const errors = [];
  if (!action || !user) errors.push('no action item');
  else {
    const gcOf = user.role === 'general_chair' && user.committee && user.committee === action.committee;
    if (!(action.ownerUid === user.uid || gcOf || user.isAdmin)) errors.push('Only the owner, or the General Chairperson, changes an action item.');
    if (!ACTION_STATUSES.includes(status)) errors.push('Status is Open, In progress or Done.');
    if (action.status === status) errors.push('It is already ' + ACTION_STATUS_LABEL[status] + '.');
  }
  const n = String(note || '').trim();
  if (n.length > 500) errors.push('The note is limited to 500 characters.');
  if (errors.length) return { ok: false, errors, patch: null };
  const history = (Array.isArray(action.history) ? action.history : []).concat([{ status: action.status, note: action.statusNote || null, by: action.statusBy || null }]).slice(-50);
  return { ok: true, errors: [], patch: { status, statusNote: n || null, statusBy: user.uid, history } };
}

export function isOverdue(action, today) {
  if (!action || action.status === 'done' || !action.due) return false;
  const t = today || new Date().toISOString().slice(0, 10);
  return String(action.due) < t;
}

// Open first (overdue first, then by due date), done last.
export function sortActions(list) {
  const l = Array.isArray(list) ? list.slice() : [];
  const rank = (a) => a.status === 'done' ? 2 : 0;
  return l.sort((a, b) => rank(a) - rank(b) || String(a.due || '9999').localeCompare(String(b.due || '9999')) || String(a.title).localeCompare(String(b.title)));
}

// ============================================================================================================
// SECTION 534 (2026-09-30, the rest of Bob's "GO"): 2. meeting documents, invitee-only; 6. the follow-up packet at
// close; 7. search across past occasions.
// ============================================================================================================
export const DOC_MAX_BYTES = 25 * 1024 * 1024;
export const DOC_TYPES = {
  'application/pdf': 'PDF', 'application/msword': 'Word', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.ms-excel': 'Excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'image/png': 'Image', 'image/jpeg': 'Image', 'text/plain': 'Text',
};
// Who may read the occasion's documents - the convener and every invitee - as the Storage rule reads it.
export function occasionPeople(session) {
  if (!session) return [];
  return Array.from(new Set([session.convenedBy].concat(session.inviteeUids || []).filter(Boolean)));
}
export function safeFileName(name) {
  const s = String(name || '').replace(/[\\/:*?"<>|#\[\]\u0000-\u001f]/g, '_').replace(/\s+/g, ' ').trim();
  return (s || 'document').slice(0, 120);
}
// 2. The convener attaches a document to an OPEN occasion. Returns the Storage path, the object's metadata the
// Storage rule requires, and the index entry that goes on the occasion (documents[]).
export function documentUpload(session, user, file, at) {
  const errors = mayMinute(session, user);
  const f = file || {};
  if (!f.name) errors.push('Choose a file.');
  if (!(f.type in DOC_TYPES)) errors.push('A meeting document is a PDF, Word, Excel, image or text file.');
  if (!(Number(f.size) > 0)) errors.push('The file is empty.');
  if (Number(f.size) >= DOC_MAX_BYTES) errors.push('A meeting document is limited to 25 MB.');
  if (errors.length) return { ok: false, errors };
  const when = at || new Date().toISOString();
  const name = safeFileName(f.name);
  const path = 'warRoom/' + session.committee + '/' + session.id + '/' + when.replace(/[^0-9]/g, '').slice(0, 14) + '-' + name;
  const people = occasionPeople(session);
  if (!people.includes(user.uid)) people.push(user.uid);   // the admin uploading during the build reads what he wrote
  return {
    ok: true, errors: [], path,
    metadata: { contentType: f.type, customMetadata: { sessionId: session.id, uploadedBy: user.uid, invitees: people.join(','), committee: session.committee } },
    entry: { name, path, size: Number(f.size), kind: DOC_TYPES[f.type], by: user.uid, byName: user.name || user.email || '', at: when },
  };
}
export function documentsPatch(session, user, entry) {
  const errors = mayMinute(session, user);
  if (!entry || !entry.path) errors.push('no document');
  if (errors.length) return { ok: false, errors, patch: null };
  const list = Array.isArray(session.documents) ? session.documents : [];
  if (list.some(d => d && d.path === entry.path)) return { ok: false, errors: ['That document is already attached.'], patch: null };
  return { ok: true, errors: [], patch: { documents: list.concat([entry]) } };
}

// 6. THE FOLLOW-UP PACKET. Everything the occasion recorded, in one piece, for every invitee: who was there, each
// agenda item with its minutes, decisions and action items, and the documents. Issued at close - it is the locked
// record - and read by each invitee from his desk. Pure: the page prints it or saves it.
export function followUpPacket(session, actions) {
  if (!session) return null;
  const acts = sortActions(Array.isArray(actions) ? actions.filter(a => a && a.sessionId === session.id) : []);
  const items = (session.agendaItems || []).concat([{ id: GENERAL_ITEM, title: 'General business' }]);
  const present = new Set((session.attendance || []).map(a => a && a.uid));
  const sections = items.map(it => ({
    id: it.id, title: it.title, claimLabel: it.claimLabel || null,
    minutes: (session.minutes && session.minutes[it.id] && session.minutes[it.id].text) || '',
    decisions: (session.decisions || []).filter(d => d && d.itemId === it.id),
    actions: acts.filter(a => (a.itemId || GENERAL_ITEM) === it.id),
  })).filter(s => s.id !== GENERAL_ITEM || s.minutes || s.decisions.length || s.actions.length);
  return {
    title: session.title, committee: session.committee, date: session.scheduledFor || String(session.convenedAt || '').slice(0, 10),
    convenedBy: session.convenedByName || session.convenedBy, closedAt: session.closedAt || null, closeNote: session.closeNote || null,
    locked: session.state === STATES.CLOSED,
    present: (session.invitees || []).filter(x => present.has(x.uid)).map(x => x.name || x.uid).concat(present.has(session.convenedBy) ? [(session.convenedByName || 'Convener') + ' (convener)'] : []),
    absent: (session.invitees || []).filter(x => !present.has(x.uid)).map(x => x.name || x.uid),
    sections, documents: (session.documents || []).map(d => ({ name: d.name, kind: d.kind, by: d.byName, at: String(d.at || '').slice(0, 10) })),
    openActions: acts.filter(a => a.status !== 'done').length,
  };
}
export function packetText(p) {
  if (!p) return '';
  const L = [];
  L.push('WAR ROOM - FOLLOW-UP PACKET', p.committee + ' - ' + p.title, 'Date: ' + (p.date || '-') + '   Convened by: ' + p.convenedBy);
  L.push(p.locked ? 'Closed ' + String(p.closedAt || '').slice(0, 10) + (p.closeNote ? ' - ' + p.closeNote : '') + '. This is the locked record.' : 'NOT CLOSED - this record can still change.');
  L.push('', 'Present: ' + (p.present.join(', ') || 'none recorded'), 'Not recorded present: ' + (p.absent.join(', ') || 'none'));
  p.sections.forEach((s, i) => {
    L.push('', (i + 1) + '. ' + s.title + (s.claimLabel && s.claimLabel !== s.title ? ' [' + s.claimLabel + ']' : ''));
    L.push('   Minutes: ' + (s.minutes ? s.minutes.replace(/\n/g, '\n            ') : 'none recorded'));
    s.decisions.forEach(d => L.push('   Decision: ' + (RESULT_LABEL[d.result] || d.result) + ' - ' + d.motion + (d.vote && d.tally ? ' (for ' + d.tally.for + ', against ' + d.tally.against + ', abstain ' + d.tally.abstain + ')' : '')));
    s.actions.forEach(a => L.push('   Action: ' + a.title + ' - ' + (a.ownerName || a.ownerUid) + (a.due ? ', due ' + a.due : '') + ' [' + (ACTION_STATUS_LABEL[a.status] || a.status) + ']'));
  });
  L.push('', 'Documents: ' + (p.documents.length ? '' : 'none'));
  p.documents.forEach(d => L.push('   ' + d.name + ' (' + d.kind + ', ' + d.at + ')'));
  L.push('', 'Open action items: ' + p.openActions);
  return L.join('\n') + '\n';
}

// 7. SEARCH ACROSS OCCASIONS - title, agenda, agenda items and their claims, minutes, decisions, close note,
// document names. Every word must appear somewhere in the occasion. Newest first; where it matched is named.
export function searchOccasions(sessions, q) {
  const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean);
  const list = Array.isArray(sessions) ? sessions.filter(Boolean) : [];
  if (!words.length) return [];
  const out = [];
  for (const s of list) {
    const fields = [
      ['title', s.title], ['agenda', s.agenda], ['close note', s.closeNote],
      ['agenda item', (s.agendaItems || []).map(i => i.title + ' ' + (i.claimLabel || '')).join(' | ')],
      ['minutes', Object.values(s.minutes || {}).map(m => m && m.text).join(' | ')],
      ['decision', (s.decisions || []).map(d => d.motion).join(' | ')],
      ['document', (s.documents || []).map(d => d.name).join(' | ')],
    ].map(([k, v]) => [k, String(v || '').toLowerCase()]);
    const all = fields.map(f => f[1]).join(' ');
    if (!words.every(w => all.includes(w))) continue;
    out.push({ session: s, where: fields.filter(([, v]) => words.some(w => v.includes(w))).map(([k]) => k) });
  }
  return out.sort((a, b) => String(b.session.convenedAt || '').localeCompare(String(a.session.convenedAt || '')));
}

// ============================================================================================================
// Record 642 (ruling 169; bubbles review 632.2 (b); expiry ledger EXP-019). Ruling 169 gave the administrator build
// access to every War Room "until the first real occasion or 10 Nov 2026, whichever comes first" - and nothing in the
// platform ended it. The END is now enforced where it counts, in firestore.rules (warRoomBuild(): the date, or the
// cutover record warRoomCutover/ended that scripts\end-warroom-build-access-2026-10-11.js writes at the first real
// occasion). This is the page's half: from the date on, the administrator is shown the War Room as any non-invitee is,
// so the screen does not offer him what the rules now refuse. The cutover record is the rules' alone to read.
// Record 645 (ruling 169.1, Bob 2026-10-12: "I need more time"): no date ends the build access; null = open-ended. A date
// put back here must be put in firestore.rules warRoomBuild() in the same commit (test-warroom-cutover.mjs holds them).
export const BUILD_ACCESS_ENDS = null;
export function buildAccessOpen(nowMs) {
  if (!BUILD_ACCESS_ENDS) return true;
  const t = typeof nowMs === 'number' ? nowMs : Date.now();
  return t < Date.parse(BUILD_ACCESS_ENDS);
}
// The user the War Room screens act for: unchanged during the build; after it, the administrator without his build access.
export function warRoomUser(user, nowMs) {
  if (!user || !user.isAdmin || buildAccessOpen(nowMs)) return user;
  return Object.assign({}, user, { isAdmin: false });
}
